import "@/governor/activation/CallsGovernorRuntimeActivation";
import "@/governor/activation/TreeGovernorRuntimeActivation";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  resolveRouteAuthority,
} from '@/governor/routing';
import { EventProvider } from '@/contexts/EventContext';
import { useMembership } from '@/contexts/MembershipContext';
import { registerPushForUser } from '@/lib/pushNotifications';
import CallNotificationPrompt from './CallNotificationPrompt';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import {
  emitCallsRuntimeEvent,
} from '@/lib/governorEventBus';
import {
  callsIncomingChannel,
} from '@/governor/calls/CallsRealtimeChannels';
import {


  getInviteContext,
  restoreInviteContextFromUrl,
  isInviteBound,
  clearInviteContext,
} from '@/lib/inviteContext';
import Header from './Header';
import Hero from './Hero';
import Pricing from './Pricing';
import TrustStrip from './TrustStrip';
import TreePreview from './TreePreview';
import CoreFeatures from './CoreFeatures';
import EngagementSection from './EngagementSection';
import Gallery from './Gallery';

import EventsSection from './EventsSection';
import FamilyNews from './FamilyNews';
import MessageBoard from './MessageBoard';
import ComparisonSection from './ComparisonSection';
import Footer from './Footer';
import Messages from './Messages';
import WelcomeBanner from './WelcomeBanner';
import InviteModal from './InviteModal';
import FamilyGraphSection from './familyGraph/FamilyGraphSection';
import FamilyActivityFeed from './FamilyActivityFeed';
import { FamilyGraphProvider, useFamilyGraph } from '@/contexts/FamilyGraphContext';
import RelationshipOnboardingModal from './RelationshipOnboardingModal';
import SharePublicModal from './SharePublicModal';
import OnboardingTour from './onboarding/OnboardingTour';
import OnboardingChecklist from './onboarding/OnboardingChecklist';
import ActivationOverlay from './onboarding/ActivationOverlay';
import GuideModal from './GuideModal';
import ResilienceMonitor from './ResilienceMonitor';
import AppRecovery from './AppRecovery';
import MemoryClusterCard from './ai/MemoryClusterCard';
import HistorianInsightsCard from './ai/HistorianInsightsCard';
import MemorySpotlightCard from './ai/MemorySpotlightCard';
import FamilyDocumentaryCard from './ai/FamilyDocumentaryCard';
import RelationshipJourneyCard from './ai/RelationshipJourneyCard';
import MemoryPlaylistCard from './ai/MemoryPlaylistCard';
import LegacyChapterCard from './ai/LegacyChapterCard';
import GenerationHandoffCard from './ai/GenerationHandoffCard';
import UnpaidDashboardGate from './UnpaidDashboardGate';




import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { HelpCircle, X as XIcon, Sparkles, Heart, Loader2, HeartHandshake } from 'lucide-react';
import { toast } from '@/components/ui/sonner';


const makeCallRoomName = (a: string, b: string) =>
  `kc-${[a, b].sort().join('-')}`.toLowerCase().replace(/[^a-z0-9-]/g, '-').slice(0, 80);

/**
 * Calls is paused pending Google Play identity verification and
 * installed-device certification.
 *
 * Dormant Calls listeners must never crash profile claims, Tree access,
 * membership hydration, messaging, or the authenticated app shell.
 */
const CALLS_RUNTIME_PAUSED_SAFE = true;

const ringIncomingCall = (incoming: any) => {
  try {
    navigator.vibrate?.([700, 250, 700, 250, 700]);
  } catch {}

  try {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('KinsCircle incoming call', {
        body: `${incoming.fromName || incoming.fromEmail} is calling you`,
        tag: 'kinscircle-incoming-call',
      });
    }
  } catch {}

  try {
    const oldTitle = document.title;
    document.title = '📞 Incoming KinsCircle call';
    setTimeout(() => { document.title = oldTitle; }, 10000);
  } catch {}
};



// First-time onboarding helpers — keyed per user so multi-account devices
// each get their own tour. All flags live in localStorage; no DB writes.
const onbKey = (uid: string, key: string) => `kc:onb:${uid}:${key}`;

const FloatingGuide: React.FC<{ onOpen: () => void }> = ({ onOpen }) => (
  <button
    onClick={onOpen}
    className="fixed bottom-4 left-4 z-40 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white shadow-lg border border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50 text-sm font-semibold"
    title="How to use KinsCircle"
  >
    <HelpCircle className="w-4 h-4" />
    Guide
  </button>
);

// Lightweight, non-blocking nudge that slides in from the bottom-left when
// a logged-in user has been idle on the page for ~45s without finishing
// the onboarding checklist. Dismissable; shows at most once per session.
const GentleReminder: React.FC<{ message: string; onDismiss: () => void }> = ({
  message,
  onDismiss,
}) => (
  <div className="fixed bottom-20 left-4 z-40 max-w-xs bg-white rounded-2xl shadow-xl border border-fuchsia-100 px-4 py-3 flex items-start gap-2.5 animate-in slide-in-from-bottom-2 fade-in duration-300">
    <div className="w-8 h-8 shrink-0 rounded-full bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center text-white">
      <Sparkles className="w-4 h-4" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold text-slate-900 leading-tight">
        {message}
      </div>
      <div className="text-[11px] text-slate-500 mt-0.5">
        Pick up where you left off — it only takes a minute.
      </div>
    </div>
    <button
      onClick={onDismiss}
      aria-label="Dismiss reminder"
      className="p-1 rounded-full hover:bg-slate-100 text-slate-500 shrink-0"
    >
      <XIcon className="w-3.5 h-3.5" />
    </button>
  </div>
);



const LayoutInner: React.FC = () => {
  const navigate = useNavigate();
  const {
    members: people,
    photos: media,
    loading,
  } = useFamilyGraph();

  const {
    isMember,
    memberEmail,
    isAuthenticated,
    profileLoading,
    profileError,
    openMembershipModal,
  } = useMembership();

  // Real Supabase auth user (null under DEV_BYPASS_AUTH). Used to gate
  // database queries that need a JWT — without this, the synthesized
  // dev-bypass email triggers a flood of failed (Status 0) HEAD/GET
  // requests against the messages table that show up in the network tab
  // and console as "Failed to fetch".
  const { user: realAuthUser } = useAuth();
  const location = useLocation();

  const routeAuthority = useMemo(
    () => resolveRouteAuthority(location.pathname),
    [location.pathname],
  );

  if (!routeAuthority.appLayoutAllowed) {
    throw new Error(
      `Route Authority blocked AppLayout ownership for ${routeAuthority.pathname}`,
    );
  }

  const isPublicLandingRoute =
    routeAuthority.publicRoute;

  const isAppRoute =
    routeAuthority.memberApplicationRoute;

  const isDashboardRoute = isAppRoute;
  const hasRealSession = !!realAuthUser;

  useEffect(() => {
    if (isPublicLandingRoute) {
      document.title = 'KinsCircle — A Safe Family Network';
      return;
    }

    if (isDashboardRoute) {
      document.title = 'My KinsCircle';
    }
  }, [isDashboardRoute, isPublicLandingRoute]);

  // ─── Paywall gating (additive, frontend access-control) ─────────────────
  // Authenticated user whose profile finished loading WITHOUT error AND who
  // is NOT a paid member / staff. These users see ONLY pricing/payment +
  // account screens; all family-tree / dashboard write surfaces are hidden
  // and any sensitive trigger (openInvite, openShare) re-routes them into
  // the existing MembershipModal instead of opening the underlying feature.
  //
  // While the profile is still loading or errored we deliberately fall
  // back to "showing nothing extra" — we do NOT show the dashboard, and we
  // do NOT show the paywall, to avoid a flash of incorrect access state.
  const isAuthenticatedUnpaid =
    isAuthenticated && !isMember && !profileLoading && !profileError;
  const profileNotReady =
    isAuthenticated && (profileLoading || !!profileError);

  const [messagesOpen, setMessagesOpen] = useState(false);
  const [messageTarget, setMessageTarget] = useState<any>(null);
  const [incomingCallTarget, setIncomingCallTarget] = useState<any>(null);

useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail || null;
      setMessageTarget(detail);
      setMessagesOpen(true);
    };

  window.addEventListener('kinscircle:open-messages', handler);

  return () => {
    window.removeEventListener('kinscircle:open-messages', handler);
  };
}, []);
  const [inviteOpen, setInviteOpen] = useState(false);
  // Optional context for the invite modal: when an "invite this person" /
  // "invite a cousin" button is tapped, we pre-fill the personal note with
  // a targeted, role-aware message (the "invite with purpose" system).
  const [inviteContext, setInviteContext] = useState<{
    personName?: string | null;
    relation?: string | null;
    targetMemberId?: string | null;
  }>({});
  const [shareOpen, setShareOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const openInvite = useCallback(
    (opts?: { personName?: string | null; relation?: string | null; targetMemberId?: string | null }) => {
      // Access-gating: unpaid signed-in users cannot send invites. Route
      // them to the membership modal so they can subscribe first.
      if (isAuthenticated && !isMember) {
        try {
          toast.message('Subscribe to invite your family', {
            description: 'Inviting relatives unlocks the moment your membership is active.',
          });
        } catch { /* ignore */ }
        openMembershipModal();
        return;
      }
      setInviteContext({
        personName: opts?.personName ?? null,
        relation: opts?.relation ?? null,
        targetMemberId: opts?.targetMemberId ?? null,
      });
      setInviteOpen(true);
    },
    [isAuthenticated, isMember, openMembershipModal],
  );




  // Pending invite picked up from /invite/{user_id} → persisted in BOTH
  // localStorage AND sessionStorage by the InviteLanding / Join pages
  // (see src/lib/inviteContext.ts). Once the user signs in or signs up,
  // we pop the relationship-onboarding modal so they get bound to the
  // existing inviter's family graph instead of getting a duplicate root.
  // If neither storage carries the payload (mobile in-app webview wiped
  // it), restoreInviteContextFromUrl() reconstructs it from the URL.
  const [pendingInvite, setPendingInvite] = useState<
    { inviterId: string; inviterName: string; inviterFamilyName?: string | null } | null
  >(null);
  const [onboardOpen, setOnboardOpen] = useState(false);
  // Lost-invite fallback: when an invite was present at signup but the
  // user closed/skipped the relationship-onboarding modal without
  // completing the binding, this flag pops a blocking "Finish connecting
  // to your family" modal so they don't fall into a separate empty tree.
  const [finishConnectingOpen, setFinishConnectingOpen] = useState(false);
  // Tracks whether we've ever heard of an invite for this auth session,
  // so the lost-invite fallback can run even after kc:pendingInvite has
  // been cleared (e.g. by a successful or skipped onboarding flow).
  const sessionInviteRef = useRef<{ inviterId: string; inviterName: string } | null>(null);

  // Onboarding tour + checklist state — keyed per user via localStorage so
  // the tour pops once on first login and the checklist tracks progress.
  const { isPaid } = useAuth();
  const userId = realAuthUser?.id || '';
  const showOnboardingUI =
    isAppRoute &&
    isAuthenticated &&
    isPaid &&
    !!userId;
  const [tourOpen, setTourOpen] = useState(false);
  const [checklistDismissed, setChecklistDismissed] = useState(false);
  const [completed, setCompleted] = useState<{ added: boolean; uploaded: boolean; invited: boolean }>({
    added: false, uploaded: false, invited: false,
  });

  // Hydrate flags + auto-open the tour on first ever login for this user.
  useEffect(() => {
    if (!showOnboardingUI) return;
    try {
      setCompleted({
        added: localStorage.getItem(onbKey(userId, 'added')) === '1',
        uploaded: localStorage.getItem(onbKey(userId, 'uploaded')) === '1',
        invited: localStorage.getItem(onbKey(userId, 'invited')) === '1',
      });
      setChecklistDismissed(localStorage.getItem(onbKey(userId, 'checklistDismissed')) === '1');
      const seen = localStorage.getItem(onbKey(userId, 'seen')) === '1';
      if (!seen) {
        setTourOpen(true);
        localStorage.setItem(onbKey(userId, 'seen'), '1');
      }
    } catch { /* ignore */ }
  }, [showOnboardingUI, userId]);
  // Celebration copy fired when a step flips from incomplete -> complete.
  // Kept short and warm so it feels rewarding without nagging.
  const CELEBRATIONS: Record<'added' | 'uploaded' | 'invited', { title: string; body: string }> = {
    added: {
      title: '🎉 First family member added!',
      body: 'Your circle just got a little bigger.',
    },
    uploaded: {
      title: '✨ Memory saved!',
      body: 'That moment will live on with your family.',
    },
    invited: {
      title: '💌 Invite sent!',
      body: 'KinsCircle is better with everyone in it.',
    },
  };

  const markComplete = useCallback(
    (key: 'added' | 'uploaded' | 'invited') => {
      if (!userId) return;
      let wasAlready = false;
      setCompleted((c) => {
        wasAlready = c[key];
        return { ...c, [key]: true };
      });
      try {
        localStorage.setItem(onbKey(userId, key), '1');
      } catch {
        /* ignore */
      }
      if (!wasAlready) {
        const msg = CELEBRATIONS[key];
        toast.success(msg.title, { description: msg.body });
      }
    },
    [userId],
  );

  // ─── Activation auto-detection (additive, non-breaking) ──────────────
  // The new ActivationOverlay reads the same `completed` flags as the
  // existing OnboardingChecklist, but users can add members or upload
  // memories through code paths that don't fire markComplete (e.g.
  // legacy quick-add modals, drag-drop uploads). When that happens we
  // quietly flip the matching flag so the overlay + checklist + tour
  // all reflect reality. No DB writes — just the existing per-user
  // localStorage flags. Invite remains a deliberate user action.
  useEffect(() => {
    if (!showOnboardingUI) return;
    if (people.length > 1 && !completed.added) markComplete('added');
    if (media.length > 0 && !completed.uploaded) markComplete('uploaded');
  }, [
    showOnboardingUI,
    people.length,
    media.length,
    completed.added,
    completed.uploaded,
    markComplete,
  ]);


  // Allow other surfaces (e.g. Header "Guide" button) to open the tour.
  useEffect(() => {
    const handler = () => setTourOpen(true);
    window.addEventListener('kc:open-guide', handler);
    return () => window.removeEventListener('kc:open-guide', handler);
  }, []);

  const dismissChecklist = useCallback(() => {
    setChecklistDismissed(true);
    if (userId) {
      try { localStorage.setItem(onbKey(userId, 'checklistDismissed'), '1'); } catch { /* ignore */ }
    }
  }, [userId]);

  // ─── Live "goals" data, derived from the family graph ────────────────────
  // hasParent = at least one person in the tree has a parent linked.
  // peopleCount / mediaCount feed the checklist's small fun goals.
  const hasParent = useMemo(
    () => people.some((p) => Array.isArray(p.parentIds) && p.parentIds.length > 0),
    [people],
  );
  const peopleCount = people.length;
  const mediaCount = media.length;

  // ─── Gentle reminder ─────────────────────────────────────────────────────
  // After ~45s of inactivity on the page, if onboarding isn't done and the
  // checklist isn't dismissed, slide a soft nudge in once per session.
  const [reminderOpen, setReminderOpen] = useState(false);
  const reminderShownRef = useRef(false);
  const allDone = completed.added && completed.uploaded && completed.invited;
  useEffect(() => {
    if (!showOnboardingUI) return;
    if (allDone) return;
    if (reminderShownRef.current) return;
    const t = window.setTimeout(() => {
      if (reminderShownRef.current) return;
      // Only nudge if the user is still on the tab.
      if (document.visibilityState !== 'visible') return;
      reminderShownRef.current = true;
      setReminderOpen(true);
    }, 45000);
    return () => window.clearTimeout(t);
  }, [showOnboardingUI, allDone]);

  // Pick a reminder line based on what's still missing. Phrasing aligns with
  // the "viral growth" re-engagement nudges spec.
  const reminderMessage = useMemo(() => {
    if (!completed.added) return 'Your family tree is waiting 🌳';
    if (!completed.uploaded) return 'Add one more memory to continue.';
    if (!completed.invited) return 'Add one more person to continue.';
    return 'Your family tree is waiting 🌳';
  }, [completed]);




  // ─── Invite pickup + binding pipeline (mobile-safe) ──────────────────────
  // Reads the pending-invite payload from BOTH localStorage AND
  // sessionStorage (resilient to mobile in-app webview → external
  // browser handoffs that wipe localStorage). If neither carries it but
  // the URL still has /invite/<uuid> or ?inviteId / ?token, restore from
  // there before giving up. Once the inviter is resolved, opens the
  // RelationshipOnboardingModal which performs the actual binding.
  const resolvePendingInvite = useCallback(async () => {
    if (!isAuthenticated || !realAuthUser?.id) return;
    // Reads localStorage first, falls back to sessionStorage, then to URL.
    const ctx = getInviteContext() ?? restoreInviteContextFromUrl();
    if (!ctx?.inviterId) return;
    // Don't pop the modal for the inviter themselves.
    if (ctx.inviterId === realAuthUser.id) {
      clearInviteContext();
      return;
    }
    // Already bound during this auth session? Skip — no need to re-prompt.
    if (isInviteBound(ctx.inviterId)) {
      sessionInviteRef.current = {
        inviterId: ctx.inviterId,
        inviterName: ctx.inviterName ?? 'A family member',
      };
      return;
    }
    // Refresh inviter info from the server so the modal title is correct
    // even if the localStorage/URL was stale.
    try {
      const { data } = await supabase
        .from('profiles')
        .select('id,name,family_name')
        .eq('id', ctx.inviterId)
        .maybeSingle();
      const resolvedName =
        (data?.name as string | null) || ctx.inviterName || 'A family member';
      sessionInviteRef.current = { inviterId: ctx.inviterId, inviterName: resolvedName };
      setPendingInvite({
        inviterId: ctx.inviterId,
        inviterName: resolvedName,
        inviterFamilyName: (data?.family_name as string | null) ?? null,
      });
      setOnboardOpen(true);
    } catch {
      // Network error — still surface the modal with whatever name we have
      // so the user can complete the binding. Spec rule: "Do not allow the
      // user to fall into a separate empty root tree when an invite was
      // present."
      sessionInviteRef.current = {
        inviterId: ctx.inviterId,
        inviterName: ctx.inviterName ?? 'A family member',
      };
      setPendingInvite({
        inviterId: ctx.inviterId,
        inviterName: ctx.inviterName ?? 'A family member',
      });
      setOnboardOpen(true);
    }
  }, [isAuthenticated, realAuthUser?.id]);

  useEffect(() => {
    resolvePendingInvite().catch((e) =>
      console.warn('[AppLayout] resolvePendingInvite failed', e),
    );
  }, [resolvePendingInvite]);

  // Spec rule: "If signup/login completes and an invite was present but
  // binding did not happen, show a blocking modal: 'Finish connecting to
  // your family' with a button 'Connect to inviter's tree.'"
  // RelationshipOnboardingModal close path:
  //   - If markInviteBound() was called (success), isInviteBound returns
  //     true and we just close out cleanly.
  //   - If user clicked "Skip for now" or otherwise closed without binding,
  //     we KEEP the inviter in sessionInviteRef and pop the blocking
  //     finish-connecting modal so they can't accidentally fall into a
  //     duplicate empty tree.
  const dismissOnboarding = useCallback(() => {
    setOnboardOpen(false);
    const sessionInv = sessionInviteRef.current;
    if (sessionInv && isInviteBound(sessionInv.inviterId)) {
      // Successful binding — done. Clear UI state and any stray storage.
      clearInviteContext();
      setPendingInvite(null);
      return;
    }
    if (sessionInv && !isInviteBound(sessionInv.inviterId)) {
      // Binding did not happen. Keep the pendingInvite around (the
      // blocking modal needs it to re-run the pipeline) and pop the
      // lost-invite fallback after a short tick so the close animation
      // of the relationship modal completes first.
      window.setTimeout(() => {
        setFinishConnectingOpen(true);
      }, 250);
      return;
    }
    // No invite in session at all — nothing to recover from.
    clearInviteContext();
    setPendingInvite(null);
  }, []);

  // "Connect to inviter's tree" button on the blocking modal — re-runs
  // the same binding pipeline by re-opening the relationship modal with
  // the same inviter context that was resolved at signup time.
  const reopenBindingFlow = useCallback(() => {
    setFinishConnectingOpen(false);
    const sessionInv = sessionInviteRef.current;
    if (!sessionInv) {
      // Defensive: should never happen, since the blocking modal only
      // shows when sessionInviteRef.current is set.
      toast.error('Could not find your invite. Please open the invite link again.');
      return;
    }
    setPendingInvite({
      inviterId: sessionInv.inviterId,
      inviterName: sessionInv.inviterName,
    });
    setOnboardOpen(true);
  }, []);




  const fetchUnread = useCallback(async () => {
    if (!isMember || !memberEmail) {
      setUnreadCount(0);
      return;
    }
    // Skip the unread-count poll entirely when there is no real auth
    // session. Under DEV_BYPASS_AUTH the synthesized "owner@kinscircleapp.com"
    // email isn't a valid identity and the request fails network-level
    // (CORS / RLS), spamming the console.
    if (!hasRealSession) {
      setUnreadCount(0);
      return;
    }
    try {
      const { count } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_email', memberEmail)
        .eq('read', false);
      setUnreadCount(count || 0);
    } catch (err) {
      // Network / CORS failures are non-fatal — just leave the count alone.
      console.warn('[AppLayout] fetchUnread failed:', err);
    }
  }, [isMember, memberEmail]);

  useEffect(() => {
    fetchUnread();
    console.log('[CALL-LISTENER]', {
      isMember,
      memberEmail,
      hasRealSession,
      channel: memberEmail ? callsIncomingChannel(memberEmail) : null,
    });
    if (!isMember || !memberEmail || !hasRealSession) return;

    /*
     * AppLayout is an observer only. It detects incoming calls but
     * never owns lifecycle state transitions.
     */
    const interval = setInterval(fetchUnread, 8000);
    return () => clearInterval(interval);
  }, [fetchUnread, isMember, memberEmail, hasRealSession]);

  useEffect(() => {
    if (!messagesOpen) fetchUnread();
  }, [messagesOpen, fetchUnread]);

  useEffect(() => {
    if (!isMember || !memberEmail) return;
    registerPushForUser(memberEmail).catch((err) => {
      console.warn('[Push] registration failed:', err);
    });
  }, [isMember, memberEmail]);






  /*
   * App-level receiver runtime.
   *
   * This listener is mounted with the authenticated application shell,
   * independent of the Messages panel. It receives both the direct
   * incoming-call broadcast and call_sessions INSERT evidence.
   */
  useEffect(() => {
    if (
      CALLS_RUNTIME_PAUSED_SAFE ||
      !isMember ||
      !memberEmail ||
      !hasRealSession
    ) {
      return;
    }

    let closed = false;

    const surfaceIncomingCall = (
      incoming: any,
      deliveryPath: string,
    ) => {
      if (
        closed ||
        !incoming?.callSessionId ||
        !incoming?.fromEmail
      ) {
        return;
      }

      emitCallsRuntimeEvent({
        source:
          'AppLayout.receiverRuntime',
        stage:
          'INCOMING_CALL_ACKNOWLEDGED',
        status:
          'success',
        callSessionId:
          incoming.callSessionId,
        callerEmail:
          incoming.fromEmail,
        receiverEmail:
          memberEmail,
        roomName:
          incoming.roomName,
        mode:
          incoming.mode,
        deliveryPath,
      });

      void receiverChannel.send({
        type:
          'broadcast',
        event:
          'incoming-call-ack',
        payload: {
          callSessionId:
            incoming.callSessionId,
          call_session_id:
            incoming.callSessionId,
          callerEmail:
            incoming.fromEmail,
          receiverEmail:
            memberEmail,
          roomName:
            incoming.roomName,
          mode:
            incoming.mode,
          acknowledgedAt:
            new Date().toISOString(),
          deliveryPath,
        },
      });

      ringIncomingCall(incoming);
      setIncomingCallTarget(incoming);
      setMessageTarget({
        email:
          incoming.fromEmail,
        name:
          incoming.fromName,
      });
      setMessagesOpen(true);
    };

    const receiverChannel =
      supabase
        .channel(
          callsIncomingChannel(
            memberEmail,
          ),
          {
            config: {
              broadcast: {
                self: false,
              },
            },
          },
        )
        .on(
          'broadcast',
          {
            event:
              'incoming-call',
          },
          payload => {
            const data =
              payload?.payload ??
              payload;

            if (
              !data ||
              String(
                data.receiverEmail ??
                memberEmail,
              )
                .trim()
                .toLowerCase() !==
                memberEmail
                  .trim()
                  .toLowerCase()
            ) {
              return;
            }

            surfaceIncomingCall(
              {
                fromEmail:
                  data.fromEmail ??
                  data.callerEmail,
                fromName:
                  data.fromName ??
                  data.callerName ??
                  data.fromEmail,
                mode:
                  data.mode === 'video'
                    ? 'video'
                    : 'voice',
                roomName:
                  data.roomName,
                callSessionId:
                  data.callSessionId ??
                  data.call_session_id,
                call_session_id:
                  data.callSessionId ??
                  data.call_session_id,
              },
              'app_shell_broadcast',
            );
          },
        )
        .on(
          'postgres_changes',
          {
            event:
              'INSERT',
            schema:
              'public',
            table:
              'call_sessions',
            filter:
              `receiver_email=eq.${memberEmail}`,
          },
          payload => {
            const row: any =
              payload?.new;

            if (
              !row?.id ||
              row.status !== 'ringing'
            ) {
              return;
            }

            surfaceIncomingCall(
              {
                fromEmail:
                  row.caller_email,
                fromName:
                  row.caller_name ??
                  row.caller_email,
                mode:
                  row.mode === 'video'
                    ? 'video'
                    : 'voice',
                roomName:
                  row.room_name,
                callSessionId:
                  row.id,
                call_session_id:
                  row.id,
              },
              'call_sessions_realtime_insert',
            );
          },
        )
        .subscribe(status => {
          if (
            status ===
            'SUBSCRIBED'
          ) {
            emitCallsRuntimeEvent({
              source:
                'AppLayout.receiverRuntime',
              stage:
                'RECEIVER_CHANNEL_SUBSCRIBED',
              status:
                'success',
              callSessionId:
                null,
              receiverEmail:
                memberEmail,
              channel:
                callsIncomingChannel(
                  memberEmail,
                ),
            });
          }
        });

    return () => {
      closed = true;

      void supabase.removeChannel(
        receiverChannel,
      );
    };
  }, [
    hasRealSession,
    isMember,
    memberEmail,
  ]);

  // HARD CALL FIX: database polling backup so receiver sees incoming calls even if realtime misses.


  // KinsCircle Call Owner: AppLayout is the ONLY receiver-side call detector.
  useEffect(() => {
    if (
      CALLS_RUNTIME_PAUSED_SAFE ||
      !isMember ||
      !memberEmail ||
      !hasRealSession
    ) {
      return;
    }

    let cancelled = false;
    let lastSeen = '';

    const isAnsweredLocally = (roomName?: string | null) => {
      if (!roomName) return false;
      try {
        const raw = localStorage.getItem(`kc:call-answering:${roomName}`);
        return !!raw && Date.now() - Number(raw) < 2 * 60 * 1000;
      } catch {
        return false;
      }
    };

    const onAnswered = (event: any) => {
      const roomName = event?.detail?.roomName;
      if (roomName) lastSeen = roomName;
    };

    window.addEventListener('kc:call-answered', onAnswered as EventListener);

    const checkIncomingCall = async () => {
      const since = new Date(Date.now() - 3 * 60 * 1000).toISOString();

      const { data, error } = await supabase
        .from('call_sessions')
        .select('id, caller_email, caller_name, mode, room_name, status, created_at')
        .eq('receiver_email', memberEmail)
        .eq('status', 'ringing')
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(1);

      if (cancelled || error || !data?.length) return;

      const row: any = data[0];
      if (!row?.id || row.id === lastSeen) return;
      if (isAnsweredLocally(row.room_name)) return;
      lastSeen = row.id;

      const incoming = {
        fromEmail: row.caller_email,
        fromName: row.caller_name || row.caller_email,
        mode: row.mode === 'video' ? 'video' : 'voice',
        roomName: row.room_name,
        callSessionId: row.id,
        call_session_id: row.id,
      };


        try {
          const suppressAt = Number(localStorage.getItem(`kc:call-answering:${incoming.roomName}`) || '0');
          if (suppressAt && Date.now() - suppressAt < 45000) return;
        } catch {}

        emitCallsRuntimeEvent({
          source:
            'AppLayout.callSessionPolling',
          stage:
            'INCOMING_CALL_DETECTED',
          status:
            'success',
          callSessionId:
            incoming.callSessionId,
          callerEmail:
            incoming.fromEmail,
          receiverEmail:
            memberEmail,
          roomName:
            incoming.roomName,
          mode:
            incoming.mode,
          deliveryPath:
            'call_sessions_polling',
        });

        console.log('[KINSCIRCLE CALL DETECTED]', incoming);
      ringIncomingCall(incoming);
      setIncomingCallTarget(incoming);
      setMessageTarget({ email: incoming.fromEmail, name: incoming.fromName });
      setMessagesOpen(true);
    };

    checkIncomingCall();
    const timer = setInterval(checkIncomingCall, 2000);

    return () => {
      cancelled = true;
      window.removeEventListener('kc:call-answered', onAnswered as EventListener);
      clearInterval(timer);
    };
  }, [isMember, memberEmail]);

const scrollTo = (id: string) => {
    if (id === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ─── Bootstrap-watchdog (universal "no blank/non-loading page" guarantee) ─
  // If FamilyContext.loading is still true after 8 seconds (e.g. invited
  // member whose RLS query hangs, slow profile fetch, or any provider
  // pipeline that didn't resolve), we flip to the AppRecovery screen so
  // the user gets a visible "We're reconnecting your family access"
  // message + Retry / Return-to-invite buttons instead of an infinite
  // spinner. This matches the spec rule: "no endless auth/profile/family
  // retry loop; timeout after 8 seconds and show recovery state."
  const [bootTimedOut, setBootTimedOut] = useState(false);
  useEffect(() => {
    if (!loading) {
      setBootTimedOut(false);
      return;
    }
    const t = window.setTimeout(() => setBootTimedOut(true), 8000);
    return () => window.clearTimeout(t);
  }, [loading]);

  // Internal-only diagnostics — never shown to the user. Helps an
  // operator see at a glance which leg of the bootstrap stalled for a
  // given account (owner / admin / paid / invited / unpaid / pending).
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.debug('[KinsCircle][Boot] state', {
      loading,
      profileLoading,
      profileError,
      isAuthenticated,
      isMember,
      hasRealSession,
      authUid: realAuthUser?.id ?? null,
      memberEmail: memberEmail ?? null,
      peopleCount: people.length,
      bootTimedOut,
    });
  }, [
    loading,
    profileLoading,
    profileError,
    isAuthenticated,
    isMember,
    hasRealSession,
    realAuthUser?.id,
    memberEmail,
    people.length,
    bootTimedOut,
  ]);

  if (loading && bootTimedOut) {
    return (
      <AppRecovery
        diagnostic={
          [
            `loading=${loading}`,
            `profileLoading=${profileLoading}`,
            `profileError=${profileError ?? 'none'}`,
            `isAuthenticated=${isAuthenticated}`,
            `authUid=${realAuthUser?.id ?? 'none'}`,
            `peopleCount=${people.length}`,
          ].join(' | ')
        }
        onRetry={() => {
          // Soft retry: clear the timed-out flag so the loading spinner
          // re-renders. The underlying providers' background loaders
          // continue to resolve and will flip `loading` to false the
          // moment they land. If they're truly wedged, hard-reload.
          setBootTimedOut(false);
          window.setTimeout(() => {
            try {
              window.location.reload();
            } catch {
              /* ignore */
            }
          }, 300);
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-fuchsia-50 to-purple-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-amber-200 shadow-xl mx-auto mb-4 bg-slate-950">
            <img
              src="/brand/kinscircle-tree-icon.png"
              alt="KinsCircle"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="font-serif text-2xl font-bold text-slate-900 mb-2">
            Kins<span className="bg-gradient-to-r from-fuchsia-600 to-pink-500 bg-clip-text text-transparent">Circle</span>
          </div>
          <div className="flex items-center justify-center gap-2 text-fuchsia-700">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Gathering your family circle...</span>
          </div>
        </div>
      </div>
    );
  }


  return (
    // ── SINGLE AUTHORITATIVE FamilyGraphProvider (Nov 2026) ─────────────
    // The provider is mounted ONCE here at the app shell, so every
    // consumer — Header (and its embedded SystemStatusPanel),
    // FamilyGraphSection's tree, FamilyActivityFeed, the AI cards, and
    // any future top-level reader — all read from the SAME provider
    // instance. This eliminates the previous "tree shows members but
    // Header/SystemStatusPanel reads an empty parallel provider and
    // reports members.length=0 / bindingState=loading" drift. The
    // provider itself only fetches when authenticated, so guests stay
    // unaffected. All previous hydration fixes (broadened OR query,
    // union owner+claimed merge, plain-eq fallback, lastRefreshError
    // surfacing) live inside the provider and apply globally to every
    // authenticated user.
    <div className="min-h-screen bg-white font-sans">
      {/* Headless background reconciliation + error-logger user-id stamper.
          Renders nothing; runs every 5 minutes when the tab is visible. */}
      <ResilienceMonitor />
      <Header
        onNavigate={scrollTo}
        onOpenMessages={(target) => { setMessageTarget(target || null); setMessagesOpen(true); }}
        unreadCount={unreadCount}
      />

      {isAppRoute && isMember && <WelcomeBanner />}
      {isPublicLandingRoute && (
        <Hero
          onExplore={() => scrollTo('graph')}
          onStart={() => {
            // Public homepage CTA: send unauthenticated visitors to the
            // public /join onboarding page; signed-in users go straight
            // to their tree.
            if (isAuthenticated) {
              navigate('/app');
            } else {
              navigate('/join');
            }
          }}
        />
      )}


      {/* Trust + Purpose: "More Than a Family Tree" */}
      {isPublicLandingRoute && <TrustStrip />}

      {/* Marketing preview of the structured family tree — only shown to
          visitors who haven't signed in yet. Once authenticated, the real
          interactive tree (FamilyGraphSection) is the single source of truth. */}
      {isPublicLandingRoute && <TreePreview />}

      {/* Three core features: Build, Share, Grow */}
      {isPublicLandingRoute && <CoreFeatures />}

      {/* Pricing/upgrade is shown ONLY to confirmed free users. */}
      {isPublicLandingRoute && <Pricing />}

      {/* ───────────── Dashboard access gating ─────────────────────────────
          ACCESS GATE: the family-tree experience is gated behind isMember
          (paid plan OR staff role). Authenticated users who have not yet
          successfully paid see UnpaidDashboardGate instead — a pricing /
          payment surface that routes them straight into MembershipModal
          with the correct billing cycle. Failed, incomplete, or canceled
          payments leave isMember === false, so those users are correctly
          NOT shown the tree, activity feed, or AI cards.

          Guests (not signed in) keep seeing the public marketing
          experience above (Hero, TrustStrip, TreePreview, CoreFeatures,
          Pricing) and never see the live tree — same as before. */}
      {isAppRoute && isMember && (
        <>
          {/* Single, unified family tree experience — "You" centered with
              mother's & father's side branches, smart suggestions, and missions.
              The tree/dashboard screen also exposes its own "Share KinsCircle"
              button, so we pass a handler through. */}
          <FamilyGraphSection
            onInvite={(opts) => openInvite(opts)}
            onShare={() => setShareOpen(true)}
          />

          {/* Family Activity Feed — reads only from the family graph; family-only
              visibility (auth-gated + RLS-bound queries). Reads from the
              SAME single authoritative FamilyGraphProvider mounted at the
              shell level — no nested provider, no separate state island. */}
          <FamilyActivityFeed />
        </>
      )}

      {/* Authenticated-but-unpaid users see the paywall gate in place of
          the dashboard. They can still scroll up to see Pricing, or use
          Header → Account / Sign out. Their family graph data is NEVER
          deleted; the moment they pay, isMember flips true and the
          dashboard re-appears with the exact same data attached. */}
      {isAppRoute && isAuthenticatedUnpaid && <UnpaidDashboardGate />}


      {/* Profile is still loading or errored — keep the dashboard hidden
          rather than flash either the paywall OR the tree, until the
          profile resolves. The Header still gives the user a way out. */}
      {isAppRoute && profileNotReady && (
        <div className="py-16 px-4 flex items-center justify-center text-amber-800/70 text-sm gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your family circle…
        </div>
      )}

      {/* Public marketing-friendly sections (Gallery, MessageBoard,
          Events, FamilyNews) — visible to guests as before, and to paid
          members. Hidden ONLY from authenticated-unpaid users so their
          dashboard view is strictly pricing/payment-only. */}
      {(isPublicLandingRoute || (isAppRoute && isMember)) && (
        <>
          <Gallery />
          <MessageBoard />
          <EventsSection />
          <FamilyNews isMember={isMember} />
        </>
      )}

      {/* AI / Legacy cards — paid-member only. Each card also internally
          renders null when its feature flag is OFF. */}
      {isAppRoute && isMember && (
        <>
          {/* Phase 6C — Semantic Memory Intelligence */}
          <MemoryClusterCard />
          <HistorianInsightsCard />

          {/* Phase 6D — Legacy Presentation Layer V1 */}
          <MemorySpotlightCard />
          <FamilyDocumentaryCard />
          <RelationshipJourneyCard />

          {/* Phase 6D.2 — Legacy Preservation + Guided Story Collections */}
          <MemoryPlaylistCard />
          <LegacyChapterCard />
          <GenerationHandoffCard />
        </>
      )}




      {isPublicLandingRoute && <ComparisonSection />}
      {/* Emotional engagement CTA */}
      {isPublicLandingRoute && (
        <EngagementSection onInvite={() => openInvite()} />
      )}

      {isPublicLandingRoute && <Footer />}
      {isAppRoute && isMember && <MemberFooter />}

      {isAppRoute && (
        <>
          <CallNotificationPrompt
            email={memberEmail}
            isMember={isMember}
          />
          
          <Messages
            open={messagesOpen}
            onOpenChange={setMessagesOpen}
            initialTarget={messageTarget}
            initialIncomingCall={incomingCallTarget}
          />
          <InviteModal
            open={inviteOpen}
            onOpenChange={(open) => {
              setInviteOpen(open);
              if (!open) {
                setInviteContext({});
              }
            }}
            prefillPersonName={
              inviteContext.personName ??
              null
            }
            prefillRelation={
              inviteContext.relation ??
              null
            }
            targetMemberId={
              inviteContext.targetMemberId ??
              null
            }
          />
          <SharePublicModal
            open={shareOpen}
            onOpenChange={setShareOpen}
          />
        </>
      )}


      {/* Relationship onboarding — pops automatically when a newly signed-in
          user has a pending invite from /invite/{user_id}. */}
      <RelationshipOnboardingModal
        open={isAppRoute && onboardOpen}
        inviter={
          pendingInvite
            ? { id: pendingInvite.inviterId, name: pendingInvite.inviterName, family_name: pendingInvite.inviterFamilyName ?? null }
            : null
        }
        onClose={dismissOnboarding}
      />

      {/* ───────────── Lost-invite fallback (mobile-safe) ─────────────────
          Spec rule: "If signup/login completes and an invite was present
          but binding did not happen, show a blocking modal: 'Finish
          connecting to your family' with a button 'Connect to inviter's
          tree.' That button must re-run the same binding pipeline using
          the stored inviteId/token."

          This is intentionally small and blocking (no overlay-click /
          escape close) so the user can't accidentally fall into a
          duplicate empty root tree when an invite was originally
          present. The single CTA re-opens RelationshipOnboardingModal
          which performs the actual binding, exactly the same pipeline
          that runs on first signup. */}
      <Dialog
        open={
          isAppRoute &&
          finishConnectingOpen
        }
        onOpenChange={(o) => {
          // Don't allow click-outside / ESC to dismiss — this is the
          // lost-invite fallback and must be resolved by binding.
          if (o) setFinishConnectingOpen(true);
        }}
      >
        <DialogContent
          className="max-w-sm bg-gradient-to-br from-fuchsia-50 to-pink-50 border-fuchsia-200 [&>button]:hidden"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >

          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-fuchsia-900 flex items-center gap-2">
              <HeartHandshake className="w-5 h-5 text-fuchsia-700" />
              Finish connecting to your family
            </DialogTitle>
            <DialogDescription className="text-fuchsia-900/70">
              {sessionInviteRef.current?.inviterName
                ? `${sessionInviteRef.current.inviterName.split(' ')[0]} invited you, but we haven't placed you in their tree yet.`
                : 'You were invited to a family, but we haven\'t placed you in their tree yet.'}{' '}
              Connect now so you appear inside the existing tree — not a separate empty one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Button
              onClick={reopenBindingFlow}
              className="w-full h-11 bg-gradient-to-r from-fuchsia-600 via-pink-500 to-amber-400 text-white font-semibold gap-2 shadow"
            >
              <Sparkles className="w-4 h-4" />
              Connect to inviter's tree
            </Button>
            <p className="text-[11px] text-center text-slate-500">
              We'll attach your account to the family member profile your
              invite came from. Your relatives' tree, photos, and memories
              stay exactly as they are.
            </p>
          </div>
        </DialogContent>
      </Dialog>



      {/* ───────────── First-time onboarding & guidance system ─────────────
          Visible only to logged-in, subscribed users. Lives entirely on the
          authenticated dashboard — never on the homepage / invite page. */}
      {showOnboardingUI && (
        <>
          <OnboardingTour
            open={tourOpen}
            onOpenChange={setTourOpen}
            completed={completed}
            markComplete={markComplete}
            onAddMember={() => scrollTo('graph')}
            onUploadMemory={() => scrollTo('graph')}
            onInviteFamily={() => openInvite()}
            onExploreTree={() => scrollTo('graph')}
          />
          {!checklistDismissed && !allDone && (
            <OnboardingChecklist
              completed={completed}
              peopleCount={peopleCount}
              mediaCount={mediaCount}
              hasParent={hasParent}
              onOpenGuide={() => setTourOpen(true)}
              onDismiss={dismissChecklist}
            />
          )}
          <FloatingGuide onOpen={() => setTourOpen(true)} />
          {reminderOpen && !allDone && (
            <GentleReminder
              message={reminderMessage}
              onDismiss={() => setReminderOpen(false)}
            />
          )}
          {/* ───────────── Activation Overlay (additive) ─────────────────
              Non-blocking "Let's build your family in 60 seconds" guide.
              Reads the SAME `completed` flags + live people/media counts
              the existing OnboardingChecklist already uses — it does not
              create or duplicate any state. Auto-opens on dashboard load
              when at least one of the three activation steps is still
              incomplete, and is fully dismissible (Esc / X / click-out).
              Listens for `kc:open-activation` so Feature Guide's
              "Continue Setup" button + the persistent corner pill can
              reopen it any time. Never blocks any user action; never
              touches tree logic, parent_ids, spouse_id, uploads, billing,
              auth, Resend, or DB schema. */}
          <ActivationOverlay
            completed={completed}
            peopleCount={peopleCount}
            mediaCount={mediaCount}
            onAddMember={() => scrollTo('graph')}
            onUploadMemory={() => scrollTo('graph')}
            onInviteFamily={() => openInvite()}
            onOpenTour={() => setTourOpen(true)}
          />
        </>
      )}


      {/* ───────────── Universal in-app Guide modal ─────────────
          Mounted ONCE for every user (auth + guest). Listens for the
          global `kc:open-help` event dispatched by:
            • Header desktop "Guide" button (top of screen, no scroll)
            • Header mobile HelpCircle icon (always-visible top bar)
            • Mobile menu "Guide — How it works" entry
            • Mobile menu "Feature Guide" entry (lands on Feature Guide tab
              via CustomEvent detail.tab='features')
          Provides four tabs: Feature Guide (searchable Help Center),
          How it works, What's New, First-time tour. Editing
          src/data/guideUpdates.ts is the only step required to ship a
          new "What's New" entry — no rebuild, no backend change.

          The Feature Guide tab's action buttons are wired to existing
          app surfaces only (scrollTo, /account, openInvite, tour reopen)
          so there are NEVER dead buttons. */}
      {isAppRoute && (
      <GuideModal
        onInviteFamily={() => openInvite()}
        onScrollTo={(id) => scrollTo(id)}
        onOpenAccount={() => navigate('/settings')}
        onContinueSetup={() => {
          // Spec: "Tie into Feature Guide → Add 'Continue Setup' entry
          // point that opens activation overlay." Dispatching the
          // additive `kc:open-activation` event reopens ActivationOverlay
          // even when it has been dismissed this session. Falls back to
          // opening the existing first-time tour for users who don't
          // qualify for the activation overlay (e.g. guests, free tier
          // visitors), which preserves the original behavior.
          window.dispatchEvent(new Event('kc:open-activation'));
          if (!showOnboardingUI) {
            setTourOpen(true);
          }
        }}
      />
      )}


    </div>
  );
};






const MemberFooter = () => (
  <footer className="mt-8 mb-28 px-6">
    <div className="rounded-3xl border border-purple-200 bg-gradient-to-br from-purple-50 via-fuchsia-50 to-white px-5 py-7 text-center shadow-sm">
      <div className="flex items-center justify-center gap-3 mb-4">
        <img src="/brand/kinscircle-tree-icon.png" alt="KinsCircle" className="h-12 w-12 rounded-xl shadow-sm" />
        <div className="text-left">
          <div className="font-serif text-2xl font-bold text-slate-900">
            Kins<span className="text-fuchsia-500">Circle</span>
          </div>
          <div className="text-xs text-slate-500">Bringing Families Together</div>
        </div>
      </div>

      <p className="mx-auto max-w-md text-sm leading-6 text-slate-600">
        Thank you for being part of the KinsCircle family. Together, we are preserving memories,
        strengthening connections, and building a lasting family legacy.
      </p>

      <div className="mt-5 text-sm font-medium text-purple-700">
        <a href="mailto:support@kinscircleapp.com">support@kinscircleapp.com</a>
      </div>

      <div className="mt-4 flex items-center justify-center gap-4 text-sm text-slate-500">
        <a href="/privacy" className="hover:text-purple-700">Privacy & Safety</a>
        <span>•</span>
        <a href="/terms" className="hover:text-purple-700">Terms of Service</a>
      </div>

      <div className="mt-5 border-t border-slate-100 pt-4 text-xs text-slate-400">
        © 2026 KinsCircle
      </div>
    </div>
  </footer>
);

const AppLayout: React.FC = () => {
  return (
    <FamilyGraphProvider>
      <EventProvider>
        <LayoutInner />
      </EventProvider>
    </FamilyGraphProvider>
  );
};

export default AppLayout;
